import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  FlaskConical,
  MapPin,
  Wifi,
  Clock,
  DollarSign,
  Users,
  Award,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Building,
  Sparkles,
  Zap,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

interface Slot {
  id: string;
  start_time: string;
  end_time: string;
  capacity: number;
  booked_count: number;
  is_locked: boolean;
}

interface StudyDetail {
  id: string;
  title: string;
  description: string;
  compensation_amount: number;
  compensation_type: string;
  duration_minutes: number;
  location: string | null;
  is_remote: boolean;
  min_reliability_score: number;
  max_participants: number;
  current_participants: number;
  status: string;
  category: string | null;
  starts_at: string;
  ends_at: string;
  criteria: Record<string, unknown>;
  researcher_name: string;
  researcher_department: string | null;
  slots?: Slot[];
}

export default function StudyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [study, setStudy] = useState<StudyDetail | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<string | null>(null); // slotId being booked
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const load = async () => {
      try {
        const [studyData, slotsData] = await Promise.all([
          api.getStudy(id),
          api.getSlots(id),
        ]);
        if (!mounted) return;
        setStudy(studyData);
        setSlots(slotsData.slots ?? slotsData ?? []);
      } catch {
        toast.error('Failed to load study');
        navigate('/studies');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [id, navigate]);

  const handleBook = async () => {
    if (!selectedSlot || !id) return;
    setBooking(selectedSlot);
    try {
      await api.bookAppointment({ study_id: id, slot_id: selectedSlot });
      toast.success('Appointment booked! Check your appointments page.');
      navigate('/appointments');
    } catch (err: any) {
      toast.error(err.message || 'Failed to book appointment');
    } finally {
      setBooking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-800 border-t-brand-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading study...</p>
        </div>
      </div>
    );
  }

  if (!study) return null;

  const spotsLeft = study.max_participants - study.current_participants;
  const availableSlots = slots.filter((s) => !s.is_locked && s.booked_count < s.capacity && new Date(s.start_time) > new Date());
  const meetsScore = (user?.reliability_score ?? 0) >= study.min_reliability_score;

  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-4xl mx-auto space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to studies
      </button>

      {/* Main Info Card */}
      <div className="card p-6 sm:p-8 relative overflow-hidden">
        {/* Top glow */}
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-10 bg-brand-500" />

        <div className="relative">
          {/* Category & Status */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {study.category && <span className="badge-brand">{study.category}</span>}
            <StatusBadge status={study.status} />
            {study.is_remote && (
              <span className="badge bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400">
                <Wifi className="w-3 h-3" /> Remote
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">{study.title}</h1>

          {/* Researcher Info */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-brand-500 flex items-center justify-center text-white font-bold text-sm shadow-md">
              {study.researcher_name?.split(' ').map(n => n[0]).join('') || 'R'}
            </div>
            <div>
              <p className="text-sm font-semibold">{study.researcher_name}</p>
              {study.researcher_department && (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Building className="w-3 h-3" /> {study.researcher_department}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="prose prose-sm dark:prose-invert max-w-none mb-6">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{study.description}</p>
          </div>

          {/* Meta Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="p-3 rounded-xl bg-success-50 dark:bg-green-900/10">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="w-4 h-4 text-success-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Compensation</span>
              </div>
              <p className="text-xl font-bold text-success-600 dark:text-success-400">${study.compensation_amount}</p>
              <p className="text-[10px] text-gray-400 capitalize">{study.compensation_type}</p>
            </div>

            <div className="p-3 rounded-xl bg-brand-50 dark:bg-brand-900/10">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="w-4 h-4 text-brand-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Duration</span>
              </div>
              <p className="text-xl font-bold text-brand-600 dark:text-brand-400">{study.duration_minutes}</p>
              <p className="text-[10px] text-gray-400">minutes</p>
            </div>

            <div className="p-3 rounded-xl bg-accent-50 dark:bg-accent-900/10">
              <div className="flex items-center gap-1.5 mb-1">
                <Users className="w-4 h-4 text-accent-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Spots Left</span>
              </div>
              <p className="text-xl font-bold text-accent-600 dark:text-accent-400">{spotsLeft}</p>
              <p className="text-[10px] text-gray-400">of {study.max_participants}</p>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center gap-1.5 mb-1">
                {study.is_remote ? <Wifi className="w-4 h-4 text-accent-500" /> : <MapPin className="w-4 h-4 text-gray-400" />}
                <span className="text-xs text-gray-500 dark:text-gray-400">Location</span>
              </div>
              <p className="text-sm font-semibold truncate">
                {study.is_remote ? 'Remote' : study.location || 'On Campus'}
              </p>
            </div>
          </div>

          {/* Reliability Requirement */}
          {study.min_reliability_score > 0 && (
            <div className={`flex items-center gap-3 p-3 rounded-xl border mb-6 ${
              meetsScore
                ? 'bg-success-50 dark:bg-green-900/10 border-success-200 dark:border-green-800'
                : 'bg-danger-50 dark:bg-red-900/10 border-danger-200 dark:border-red-800'
            }`}>
              {meetsScore ? (
                <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-danger-500 flex-shrink-0" />
              )}
              <div>
                <p className="text-sm font-medium">
                  {meetsScore ? 'You meet the reliability requirement' : 'Reliability score too low'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Minimum score: {study.min_reliability_score} · Your score: {(user?.reliability_score ?? 0).toFixed(1)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Section */}
      {user?.role === 'student' && study.status === 'active' && spotsLeft > 0 && meetsScore && (
        <div className="card p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-brand-500" />
            <h2 className="section-header">Book a Time Slot</h2>
          </div>

          {availableSlots.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No available time slots right now</p>
              <p className="text-xs text-gray-400 mt-1">Check back later or contact the researcher</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {availableSlots.map((slot) => {
                  const remaining = slot.capacity - slot.booked_count;
                  const isSelected = selectedSlot === slot.id;
                  return (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedSlot(isSelected ? null : slot.id)}
                      className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                        isSelected
                          ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-400 dark:border-brand-600 shadow-md shadow-brand-500/10'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-300 dark:hover:border-brand-700'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-brand-500" />
                          <span className="text-sm font-semibold">
                            {new Date(slot.start_time).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        </div>
                        {isSelected && <CheckCircle className="w-5 h-5 text-brand-500" />}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(slot.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {' '}&ndash;{' '}
                        {new Date(slot.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {remaining} spot{remaining !== 1 ? 's' : ''} remaining
                      </p>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleBook}
                disabled={!selectedSlot || booking !== null}
                className="btn-primary w-full sm:w-auto"
              >
                {booking ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Booking...
                  </div>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Book This Slot
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Not eligible message */}
      {user?.role === 'student' && !meetsScore && study.min_reliability_score > 0 && (
        <div className="card p-6 border-danger-200 dark:border-danger-800 bg-danger-50/50 dark:bg-red-900/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-danger-600 dark:text-danger-400">Not Eligible</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                This study requires a reliability score of at least {study.min_reliability_score}. Complete more studies and avoid no-shows to improve your score.
              </p>
              <Link to="/profile" className="text-sm text-brand-500 hover:text-brand-600 mt-2 inline-block">
                View your profile
              </Link>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'badge bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    active: 'badge-success',
    paused: 'badge-warning',
    completed: 'badge-brand',
    cancelled: 'badge-danger',
  };
  return <span className={styles[status] || 'badge'}>{status}</span>;
}
